import React, { useEffect, useState, useRef, useLayoutEffect, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SendIcon, PaperclipIcon, ImageIcon, FileIcon, XIcon, ChevronLeftIcon, UserIcon, PhoneIcon, VideoIcon, InfoIcon, MessageSquareIcon, MoreVerticalIcon, ArchiveIcon, ArchiveRestoreIcon, InboxIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { Conversation, Message, getUserConversations, subscribeToUserConversations, subscribeToConversationMessages, sendMessage, markMessagesAsRead, createConversation, getTeacherById, getConversation, archiveConversation, unarchiveConversation } from '../../services/messagingService';
export const StudentMessages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    loading: authLoading
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  // Note: Optimistic-message architecture was removed to match the smoother
  // teacher-side flow. Messages are now server-authoritative: send → await
  // Firestore → snapshot delivers the bubble. One DOM mount per message,
  // no temp/server identity swap, no merge/dedup logic in the snapshot
  // reducer. This is what makes TeacherMessages feel jitter-free.
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Track whether messages are loading for the selected conversation (prevents empty-state flash).
  // Initialize TRUE so the right panel doesn't briefly render the "Start a conversation" empty
  // state during the first paint after auto-selecting a conversation. Without this, the panel
  // visibly flashes empty-state → loading → messages (the "double-jump" regression).
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  // Lightbox state for image viewer
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeConversationIdRef = useRef<string | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  const [isSending, setIsSending] = useState(false);
  // NEW: Track user scroll position to avoid interrupting manual scrolling
  const userScrollPositionRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
    isNearBottom: boolean;
    hasManuallyScrolled: boolean;
  }>({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
    isNearBottom: true,
    hasManuallyScrolled: false
  });
  // Flag to track if we should force scroll after sending
  const shouldForceScrollRef = useRef(false);
  // NEW: Use a single scroll operation ID to prevent race conditions
  const scrollOperationIdRef = useRef(0);
  // Track last message count to determine if new messages arrived
  const lastMessageCountRef = useRef(0);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Check for teacherId in query params (for direct messaging from teacher profile)
  const queryParams = new URLSearchParams(location.search);
  const teacherIdParam = queryParams.get('teacherId');
  // NEW: Better mobile state tracking
  const [mobileState, setMobileState] = useState({
    isKeyboardOpen: false,
    isTyping: false,
    viewportHeight: window.innerHeight // Track viewport height for keyboard detection
  });
  // Archive UI state (student-side only)
  const [archivedView, setArchivedView] = useState(false);
  const [menuOpenForId, setMenuOpenForId] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      // NEW: Update viewport height on resize
      if (mobile) {
        setMobileState(prev => ({
          ...prev,
          viewportHeight: window.innerHeight
        }));
      }
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Set mounted ref for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Handle direct teacher contact from profile page
  const handleTeacherContact = async (teacherId: string) => {
    try {
      console.log('Starting teacher contact process with teacherId:', teacherId);
      // Check if we already have a conversation with this teacher
      const existingConversation = conversations.find(conv => conv.teacherId === teacherId);
      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation.id);
        // Use existing conversation
        handleSelectConversation(existingConversation);
      } else {
        console.log('No existing conversation found, creating new one');
        // Create a new conversation
        const teacher = await getTeacherById(teacherId);
        if (!teacher) {
          console.error('Teacher not found with ID:', teacherId);
          setError('Teacher not found');
          return;
        }
        console.log('Teacher data retrieved:', teacher.fullName);
        // Create conversation with proper fallbacks for all required fields
        const conversationId = await createConversation(teacherId, teacher.fullName || 'Teacher', teacher.profilePictureUrl || '', currentUser.uid, currentUser.displayName || 'Student', currentUser.photoURL || '', teacher.instruments && teacher.instruments.length > 0 ? teacher.instruments[0] : 'Music');
        console.log('New conversation created with ID:', conversationId);
        // Refresh conversations to include the new one
        const updatedConversations = await getUserConversations(currentUser.uid, 'student');
        setConversations(updatedConversations);
        // Find and select the new conversation
        const newConversation = updatedConversations.find(conv => conv.id === conversationId);
        if (newConversation) {
          console.log('Found and selecting new conversation');
          handleSelectConversation(newConversation);
        } else {
          console.error('Created conversation not found in updated list');
          try {
            // Force a direct fetch of the conversation to handle any timing issues
            const directFetchedConversation = await getConversation(conversationId);
            if (directFetchedConversation) {
              setConversations([...updatedConversations, directFetchedConversation]);
              handleSelectConversation(directFetchedConversation);
            }
          } catch (fetchErr) {
            console.error('Error fetching new conversation:', fetchErr);
            // Handle the error gracefully without crashing the component
            setError('Error loading conversation. Please try refreshing the page.');
          }
        }
      }
      // On mobile, explicitly hide the conversation list to show the message area
      if (isMobileView) {
        setShowConversationList(false);
      }
    } catch (err) {
      console.error('Error handling teacher contact:', err);
      setError('Failed to start conversation with teacher. Please try again.');
    }
  };
  // Fetch conversations on component mount
  useEffect(() => {
    // Wait for Firebase Auth to finish restoring session before checking auth
    if (authLoading) return;
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    const fetchConversations = async () => {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        console.log('Fetching conversations for user:', currentUser.uid);
        // Verify user object has required properties
        if (!currentUser.uid) {
          console.error('Current user missing UID');
          setError('Authentication error. Please try logging in again.');
          setLoading(false);
          return;
        }
        // Get conversations first
        const userConversations = await getUserConversations(currentUser.uid, 'student');
        console.log('Fetched conversations:', userConversations.length);
        // Filter out invalid conversations
        const validConversations = userConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
        if (validConversations.length < userConversations.length) {
          console.warn(`Filtered out ${userConversations.length - validConversations.length} invalid conversations`);
        }
        // IMPROVED: Make sure conversations are sorted by lastMessageTimestamp (newest first)
        const sortedConversations = sortConversationsByTimestamp(validConversations);
        // Always set conversations (even if empty) and clear loading state
        if (isMountedRef.current) {
          setConversations(sortedConversations);
          setLoading(false);
          // Auto-select first conversation on desktop if none selected
          if (!isMobileView && sortedConversations.length > 0 && !selectedConversation) {
            setSelectedConversation(sortedConversations[0]);
            activeConversationIdRef.current = sortedConversations[0].id;
          }
        }
        // If there's a teacherId in the URL, handle it after conversations are loaded
        if (teacherIdParam) {
          console.log('Found teacherId in URL params:', teacherIdParam);
          handleTeacherContact(teacherIdParam);
        }
        // RETRY: If initial fetch returned empty, retry once after a delay
        // This handles the case where the auth token hasn't fully propagated yet
        if (sortedConversations.length === 0 && isMountedRef.current) {
          console.log('[Messaging] Initial fetch returned empty, scheduling retry in 1.5s...');
          retryTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;
            try {
              console.log('[Messaging] Retrying conversation fetch...');
              const retryConversations = await getUserConversations(currentUser.uid, 'student');
              const validRetry = retryConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
              const sortedRetry = sortConversationsByTimestamp(validRetry);
              if (isMountedRef.current && sortedRetry.length > 0) {
                console.log(`[Messaging] Retry succeeded: ${sortedRetry.length} conversations`);
                setConversations(sortedRetry);
                // Auto-select first conversation on desktop if none selected
                if (!isMobileView && !selectedConversation) {
                  setSelectedConversation(sortedRetry[0]);
                  activeConversationIdRef.current = sortedRetry[0].id;
                }
              } else {
                console.log('[Messaging] Retry also returned empty');
              }
            } catch (retryErr) {
              console.warn('[Messaging] Retry fetch failed:', retryErr);
            }
          }, 1500);
        }
      } catch (err) {
        console.error('Error fetching conversations:', err);
        // More detailed error logging
        if (err instanceof Error) {
          console.error('Error details:', err.message);
          console.error('Error stack:', err.stack);
        }
        if (isMountedRef.current) {
          setError('Failed to load conversations. Please try again.');
          setLoading(false); // Make sure to set loading to false even on error
          // RETRY on error: schedule a retry after delay
          console.log('[Messaging] Initial fetch errored, scheduling retry in 2s...');
          retryTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;
            try {
              const retryConversations = await getUserConversations(currentUser.uid, 'student');
              const validRetry = retryConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
              const sortedRetry = sortConversationsByTimestamp(validRetry);
              if (isMountedRef.current && sortedRetry.length > 0) {
                setConversations(sortedRetry);
                setError('');
                // Auto-select first conversation on desktop if none selected
                if (!isMobileView && !selectedConversation) {
                  setSelectedConversation(sortedRetry[0]);
                  activeConversationIdRef.current = sortedRetry[0].id;
                }
              }
            } catch (retryErr) {
              console.warn('[Messaging] Retry fetch failed:', retryErr);
            }
          }, 2000);
        }
      }
    };
    fetchConversations();
    // Set up subscription after initial fetch
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = subscribeToUserConversations(currentUser.uid, fetchedConversations => {
        console.log('Subscription update - conversations:', fetchedConversations.length);
        // Filter out invalid conversations
        const validConversations = fetchedConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
        // IMPROVED: Make sure conversations are sorted by lastMessageTimestamp (newest first)
        const sortedConversations = sortConversationsByTimestamp(validConversations);
        if (isMountedRef.current) {
          // Always update with whatever data the subscription provides
          // If subscription returns data, use it; if empty, only keep prev if prev has data
          setConversations(prev => sortedConversations.length > 0 ? sortedConversations : prev.length > 0 ? prev : sortedConversations);
          // Auto-select first conversation on desktop if none selected
          if (!isMobileView && sortedConversations.length > 0 && !selectedConversation) {
            setSelectedConversation(sortedConversations[0]);
            activeConversationIdRef.current = sortedConversations[0].id;
          }
          // Clear any error if we successfully got an update with data
          if (sortedConversations.length > 0 && error) setError('');
          // Ensure loading is set to false on subscription update
          if (loading) setLoading(false);
        }
      }, 'student');
    } catch (subErr) {
      console.error('Error setting up conversation subscription:', subErr);
      // Set loading to false if subscription fails
      if (isMountedRef.current && loading) {
        setLoading(false);
      }
    }
    // Safety timeout to ensure loading state doesn't get stuck
    const safetyTimeout = setTimeout(() => {
      if (isMountedRef.current && loading) {
        console.log('Safety timeout triggered - forcing loading state to false');
        setLoading(false);
      }
    }, 5000); // 5 second safety timeout
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
      clearTimeout(safetyTimeout);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [currentUser, authLoading, navigate, teacherIdParam]); // Removed error and loading from dependencies
  // NEW: Helper function to sort conversations by timestamp
  const sortConversationsByTimestamp = (conversations: Conversation[]): Conversation[] => {
    return [...conversations].sort((a, b) => {
      // Get timestamp values, properly handling Firestore timestamps
      const getTimestampValue = (timestamp: any): number => {
        // For Firestore timestamps with toDate method
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
          try {
            return timestamp.toDate().getTime();
          } catch (e) {
            console.error('Error converting Firestore timestamp:', e);
            return 0;
          }
        }
        // For Date objects
        if (timestamp instanceof Date) {
          return timestamp.getTime();
        }
        // For numeric timestamps
        if (typeof timestamp === 'number') {
          return timestamp;
        }
        // Default case - use 0 to ensure consistent sorting
        return 0;
      };
      const timeA = getTimestampValue(a.lastMessageTimestamp);
      const timeB = getTimestampValue(b.lastMessageTimestamp);
      // Sort in descending order (newest first)
      return timeB - timeA;
    });
  };
  // Subscribe to messages when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      activeConversationIdRef.current = null;
      return;
    }
    // Validate the selected conversation has all required fields
    if (!selectedConversation.id || !selectedConversation.teacherId) {
      console.error('Selected conversation is missing critical fields:', selectedConversation);
      setError('Invalid conversation data. Please try selecting another conversation.');
      return;
    }
    console.log('Setting up message subscription for conversation:', selectedConversation.id);
    // Ensure the active conversation ID is set properly
    activeConversationIdRef.current = selectedConversation.id;
    setMessagesLoading(true); // Show loading state until first snapshot
    let isFirstSnapshot = true;
    // NEW: Reset scroll position tracking when conversation changes
    userScrollPositionRef.current = {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      isNearBottom: true,
      hasManuallyScrolled: false
    };
    const unsubscribe = subscribeToConversationMessages(selectedConversation.id, fetchedMessages => {
      console.log(`Received ${fetchedMessages.length} messages for conversation ${selectedConversation.id}`);
      // Only update if this is still the active conversation and component is mounted
      if (activeConversationIdRef.current === selectedConversation.id && isMountedRef.current) {
        // Track message count for the scroll-anchor useLayoutEffect.
        lastMessageCountRef.current = fetchedMessages.length;
        // Server-authoritative: just sort and set. No optimistic merge,
        // no content-match dedup, no temp-id swap. The scroll-anchor
        // useLayoutEffect keyed on messages.length handles the scroll
        // synchronously before paint. The reveal useLayoutEffect flips
        // messagesLoading=false on the first snapshot.
        const sortedMessages = sortMessagesByTimestamp(fetchedMessages);
        setMessages(sortedMessages);
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
        }
      }
    });
    // Mark messages as read
    if (currentUser?.uid) {
      markMessagesAsRead(selectedConversation.id, currentUser.uid).catch(err => console.error('Error marking messages as read:', err));
    }
    // NEW: Add scroll event listener to track user scrolling
    const messageContainer = messageContainerRef.current;
    if (messageContainer) {
      messageContainer.addEventListener('scroll', updateScrollPosition);
    }
    return () => {
      unsubscribe();
      // Reset message count when unsubscribing
      lastMessageCountRef.current = 0;
      // Remove scroll event listener
      if (messageContainer) {
        messageContainer.removeEventListener('scroll', updateScrollPosition);
      }
    };
    // Stable primitive deps (matches TeacherMessages pattern): prevents the
    // subscription from tearing down and re-subscribing when the parent
    // conversation snapshot reassigns selectedConversation to a new object
    // reference with the same id.
  }, [selectedConversation?.id, currentUser?.uid]);
  // Synchronously scroll to bottom before paint, then reveal the panel.
  // Runs in the same frame as the first messages render, so the user never
  // sees an unscrolled state — the panel appears already positioned.
  useLayoutEffect(() => {
    if (!messagesLoading) return;
    if (!selectedConversation) return;
    if (messages.length === 0) return;
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
      updateScrollPosition();
    }
    setMessagesLoading(false);
  }, [messages, messagesLoading, selectedConversation]);
  // More consistent timestamp normalization with better fallbacks
  const getTimestampValue = (timestamp: any): number => {
    try {
      // For Firestore timestamps with toDate method
      if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        try {
          return timestamp.toDate().getTime();
        } catch (e) {
          console.error('Error converting Firestore timestamp:', e);
          // Use a consistent fallback - beginning of today instead of current time
          return new Date().setHours(0, 0, 0, 0);
        }
      }
      // For Date objects
      if (timestamp instanceof Date) {
        return timestamp.getTime();
      }
      // For numeric timestamps (e.g., from optimistic messages using Date.now())
      if (typeof timestamp === 'number') {
        return timestamp;
      }
      // For "Just now" string from optimistic messages - use current time
      if (timestamp === 'Just now') {
        return Date.now();
      }
      // For other string timestamps, try to parse
      if (typeof timestamp === 'string') {
        // Try to extract time from strings like "2:30 PM"
        const timeMatch = timestamp.match(/(\d+):(\d+)\s*([AP]M)?/i);
        if (timeMatch) {
          const now = new Date();
          let hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);
          const period = timeMatch[3]?.toUpperCase();
          // Convert to 24-hour format
          if (period === 'PM' && hours < 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          // Set the time but keep today's date
          now.setHours(hours, minutes, 0, 0);
          return now.getTime();
        }
        // Try to parse as a date string
        const parsedDate = new Date(timestamp);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getTime();
        }
      }
      // Default fallback - use a fixed reference point instead of current time
      // This ensures stable sorting even with unparseable timestamps
      return new Date().setHours(0, 0, 0, 0); // beginning of today
    } catch (error) {
      console.error('Error in getTimestampValue:', error);
      // Ultimate fallback - use beginning of today as reference
      return new Date().setHours(0, 0, 0, 0);
    }
  };
  // More reliable message sorting with better error handling
  const sortMessagesByTimestamp = (messages: Message[]): Message[] => {
    try {
      return [...messages].sort((a, b) => {
        try {
          // Get numeric values for comparison
          const timeA = getTimestampValue(a.timestamp);
          const timeB = getTimestampValue(b.timestamp);
          // Sort by timestamp
          return timeA - timeB;
        } catch (error) {
          console.error('Error comparing messages:', error);
          // Return 0 to keep relative order in case of error
          return 0;
        }
      });
    } catch (error) {
      console.error('Error sorting messages:', error);
      // Return original array if sorting fails
      return [...messages];
    }
  };
  // NEW: Track scroll position to detect if user has manually scrolled
  const updateScrollPosition = () => {
    if (messageContainerRef.current) {
      const container = messageContainerRef.current;
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = container;
      // Consider the user at the bottom if they're within 100px of the bottom
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      // Update the ref with current values
      userScrollPositionRef.current = {
        scrollTop,
        scrollHeight,
        clientHeight,
        isNearBottom,
        // Only set hasManuallyScrolled if they've scrolled away from bottom
        hasManuallyScrolled: userScrollPositionRef.current.hasManuallyScrolled ? !isNearBottom : false
      };
    }
  };
  // Single synchronous scroll — no rAF, no setTimeout, no races.
  // The useLayoutEffect keyed on messages.length handles message-append scroll.
  // This function is retained for explicit/imperative scrolls (image onLoad,
  // input focus on mobile, orientation change, first-snapshot reveal).
  const scrollToBottom = (force = false) => {
    if (!messageContainerRef.current || !isMountedRef.current) return;
    // Don't scroll if user has manually scrolled up, unless forced
    if (!force && userScrollPositionRef.current.hasManuallyScrolled) return;
    messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    updateScrollPosition();
    if (force) {
      userScrollPositionRef.current.hasManuallyScrolled = false;
    }
  };
  // Handle conversation selection
  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
    activeConversationIdRef.current = conversation.id;
    // Reset message count when selecting a new conversation
    lastMessageCountRef.current = 0;
    setMessagesLoading(true); // Show loading state until first snapshot arrives
    // NEW: Reset scroll tracking
    userScrollPositionRef.current = {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      isNearBottom: true,
      hasManuallyScrolled: false
    };
    // On mobile, hide the conversation list
    if (isMobileView) {
      setShowConversationList(false);
    }
    // Mark messages as read
    markMessagesAsRead(conversation.id, currentUser?.uid || '');
  };
  // NEW: Completely revised message sending with better scroll coordination
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile || !selectedConversation || !currentUser || isSending) {
      return;
    }
    try {
      setIsSending(true);
      // Always force scroll after sending a message
      shouldForceScrollRef.current = true;
      // Reset manual scroll tracking - user wants to see their sent message
      userScrollPositionRef.current.hasManuallyScrolled = false;
      // Reset typing state
      setMobileState(prev => ({
        ...prev,
        isTyping: false
      }));
      // Create a local copy of the message text before clearing it
      const messageText = newMessage.trim() || undefined;
      const conversationId = selectedConversation.id;
      const senderId = currentUser.uid;
      const receiverId = selectedConversation.teacherId;
      // Log message details for debugging
      console.log('Preparing to send message:', {
        conversationId,
        senderId,
        receiverId,
        messageText: messageText || '(empty)',
        hasAttachment: !!selectedFile
      });
      // Verify we have all required fields
      if (!conversationId) {
        console.error('Missing conversationId for message send');
        throw new Error('Missing conversation ID');
      }
      if (!senderId) {
        console.error('Missing senderId for message send');
        throw new Error('Missing sender ID');
      }
      if (!receiverId) {
        console.error('Missing receiverId for message send');
        throw new Error('Missing receiver ID');
      }
      // Clear the input immediately for better UX
      setNewMessage('');
      // Server-authoritative send: no temp bubble. The Firestore snapshot
      // listener will deliver the real message into `messages` once it lands,
      // and the scroll-anchor useLayoutEffect will scroll on append.
      // NEW: Better keyboard handling on mobile
      if (isMobileView) {
        // Don't blur input on iOS as it causes visual glitches
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (!isIOS && messageInputRef.current) {
          messageInputRef.current.blur();
        }
      }
      let attachment = undefined;
      if (selectedFile) {
        // Determine file type
        const fileType = selectedFile.type.startsWith('image/') ? 'image' : selectedFile.type.includes('pdf') ? 'pdf' : selectedFile.type.includes('audio') ? 'audio' : selectedFile.type.includes('video') ? 'video' : 'doc';
        attachment = {
          file: selectedFile,
          type: fileType as 'image' | 'pdf' | 'audio' | 'video' | 'doc'
        };
        console.log('Attachment details:', {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          fileSize: selectedFile.size,
          mappedType: fileType
        });
      }
      // Scroll is now handled by the useLayoutEffect keyed on messages.length —
      // it runs synchronously before paint, so no setTimeout race is needed here.
      // Send the message to Firestore
      console.log('Calling sendMessage with params:', {
        conversationId,
        senderId,
        receiverId,
        messageText: messageText || '(empty)',
        hasAttachment: !!attachment
      });
      try {
        const messageId = await sendMessage(conversationId, senderId, receiverId, messageText, attachment);
        console.log(`Message sent successfully with ID: ${messageId}`);
        // Clear file selection — the bubble will appear from the snapshot listener.
        setSelectedFile(null);
        setFilePreview(null);
        // Clear any previous errors
        if (error) setError('');
      } catch (sendError) {
        console.error('Error in sendMessage function:', sendError);
        if (isMountedRef.current) {
          setError(`Failed to send message: ${sendError.message || 'Unknown error'}. Please try again.`);
        }
        throw sendError; // Re-throw to be caught by the outer catch
      }
    } catch (err) {
      console.error('Error sending message:', err);
      // More detailed error logging
      if (err instanceof Error) {
        console.error('Error details:', err.message);
        console.error('Error stack:', err.stack);
      }
      if (isMountedRef.current) {
        // Show a more specific error message
        setError(err instanceof Error ? `Failed to send message: ${err.message}` : 'Failed to send message. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSending(false);
        // Scroll is handled by useLayoutEffect on messages.length growth.
        // Image attachments additionally trigger scrollToBottom on <img onLoad>.
      }
    }
  };
  // NEW: Improved input change handler with better typing detection
  const handleMessageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    // Only update typing state when it changes
    const isCurrentlyTyping = value.trim() !== '';
    if (isCurrentlyTyping !== mobileState.isTyping) {
      setMobileState(prev => ({
        ...prev,
        isTyping: isCurrentlyTyping
      }));
    }
  };
  // NEW: Improved keyboard handling for mobile
  const handleInputFocus = () => {
    if (isMobileView) {
      // Update keyboard state
      setMobileState(prev => ({
        ...prev,
        isKeyboardOpen: true
      }));
      // Reset manual scroll tracking when keyboard opens
      userScrollPositionRef.current.hasManuallyScrolled = false;
      // Scroll to bottom with a delay to account for keyboard animation
      // Different delays for iOS vs Android
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const scrollDelay = isIOS ? 300 : 250;
      setTimeout(() => {
        if (isMountedRef.current) {
          scrollToBottom(true);
        }
      }, scrollDelay);
    }
  };
  // NEW: Improved keyboard handling for when input loses focus
  const handleInputBlur = () => {
    if (isMobileView) {
      // Use a short delay to ensure other actions complete first
      setTimeout(() => {
        if (isMountedRef.current) {
          setMobileState(prev => ({
            ...prev,
            isKeyboardOpen: false,
            // Only keep typing state if there's text
            isTyping: newMessage.trim() !== ''
          }));
        }
      }, 100);
    }
  };
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Check file size for mobile (5MB limit for mobile vs 10MB for desktop)
      const maxSize = isMobileView ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const sizeInMB = maxSize / (1024 * 1024);
        alert(`File size exceeds ${sizeInMB}MB limit on ${isMobileView ? 'mobile' : 'desktop'}`);
        return;
      }
      setSelectedFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => {
          if (isMountedRef.current) {
            setFilePreview(e.target?.result as string);
          }
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };
  // Handle file removal
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Handle back button on mobile
  const handleBackToList = () => {
    setShowConversationList(true);
  };
  // More reliable timestamp formatting
  const formatTime = (timestamp: any) => {
    try {
      // Handle string timestamps
      if (typeof timestamp === 'string') {
        // If it's already formatted (like "2:30 PM"), return it directly
        if (/\d+:\d+\s*([AP]M)?/i.test(timestamp)) {
          return timestamp;
        }
        // Otherwise try to parse it
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        // If parsing fails, return as is
        return timestamp;
      }
      // Handle numeric timestamps (from optimistic messages)
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // Handle Firestore timestamps
      if (timestamp && typeof timestamp.toDate === 'function') {
        try {
          return timestamp.toDate().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (e) {
          console.error('Error formatting Firestore timestamp:', e);
          return 'Just now';
        }
      }
      // Default fallback
      return 'Just now';
    } catch (error) {
      console.error('Error in formatTime:', error);
      return 'Just now';
    }
  };
  // Check if the user is a student
  useEffect(() => {
    const checkUserType = async () => {
      if (currentUser) {
        // Here you would typically check if the user is a student
        // For now, we'll assume they are
      }
    };
    checkUserType();
  }, [currentUser]);
  // Single deterministic scroll: when messages.length grows AND the user was
  // at the bottom (or a send forced it), set scrollTop synchronously before
  // paint. One write, zero races. This is the ONLY message-append scroll path;
  // it replaces all prior setTimeout/rAF scroll attempts inside
  // handleSendMessage and the onSnapshot subsequent-snapshot branch.
  // Uses its own length tracker (prevRenderedMessagesLengthRef) so it stays
  // independent of lastMessageCountRef, which the onSnapshot callback owns.
  const prevRenderedMessagesLengthRef = useRef(0);
  useLayoutEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;
    const prevLen = prevRenderedMessagesLengthRef.current;
    prevRenderedMessagesLengthRef.current = messages.length;
    if (messages.length === 0) return;
    const grew = messages.length > prevLen;
    if (!grew) return;
    const shouldScroll = shouldForceScrollRef.current || userScrollPositionRef.current.isNearBottom || !userScrollPositionRef.current.hasManuallyScrolled;
    if (!shouldScroll) return;
    container.scrollTop = container.scrollHeight;
    updateScrollPosition();
    shouldForceScrollRef.current = false;
  }, [messages.length]);
  // NEW: Handle mobile orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      if (isMobileView) {
        // Update viewport height
        setMobileState(prev => ({
          ...prev,
          viewportHeight: window.innerHeight
        }));
        // Scroll to bottom after orientation change completes
        setTimeout(() => {
          if (isMountedRef.current && selectedConversation) {
            scrollToBottom(true);
          }
        }, 300);
      }
    };
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobileView, selectedConversation]);
  // Close 3-dot menu when clicking outside
  useEffect(() => {
    if (!menuOpenForId) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-conv-menu]')) {
        setMenuOpenForId(null);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [menuOpenForId]);
  // Split conversations into active / archived buckets for the student
  const activeConversations = conversations.filter(c => c.archivedForStudent !== true);
  const archivedConversations = conversations.filter(c => c.archivedForStudent === true);
  // Clear selected conversation when it leaves the visible bucket
  useEffect(() => {
    if (!selectedConversation) return;
    const visibleBucket = archivedView ? archivedConversations : activeConversations;
    const stillVisible = visibleBucket.some(c => c.id === selectedConversation.id);
    if (!stillVisible) {
      setSelectedConversation(null);
      activeConversationIdRef.current = null;
      setMessages([]);
      if (isMobileView) {
        setShowConversationList(true);
      }
    }
  }, [archivedView, conversations, selectedConversation?.id]);
  // Archive handler
  const handleArchiveConversation = async (conversationId: string) => {
    if (!currentUser?.uid || actioningId) return;
    setActioningId(conversationId);
    setMenuOpenForId(null);
    try {
      await archiveConversation(conversationId, currentUser.uid, 'student');
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        activeConversationIdRef.current = null;
        setMessages([]);
        if (isMobileView) {
          setShowConversationList(true);
        }
      }
    } catch (err) {
      console.error('[StudentMessages] Failed to archive conversation:', err);
      alert('Failed to archive conversation. Please try again.');
    } finally {
      setActioningId(null);
    }
  };
  // Unarchive handler
  const handleUnarchiveConversation = async (conversationId: string) => {
    if (!currentUser?.uid || actioningId) return;
    setActioningId(conversationId);
    setMenuOpenForId(null);
    try {
      await unarchiveConversation(conversationId, currentUser.uid, 'student');
    } catch (err) {
      console.error('[StudentMessages] Failed to unarchive conversation:', err);
      alert('Failed to unarchive conversation. Please try again.');
    } finally {
      setActioningId(null);
    }
  };
  // Compute the bucket currently visible in the list
  const visibleBucket = archivedView ? archivedConversations : activeConversations;
  // Redirect if not logged in — wait for auth to finish loading first
  if (authLoading) {
    return null;
  }
  if (!currentUser) {
    navigate('/student-login');
    return null;
  }
  // Define text color classes based on theme
  const textColorClass = isDark ? 'text-white' : 'text-gray-800';
  const subtextColorClass = isDark ? 'text-gray-300' : 'text-gray-600';
  const lightTextColorClass = isDark ? 'text-gray-300' : 'text-gray-500';
  const extraLightTextColorClass = isDark ? 'text-gray-400' : 'text-gray-500';
  // Define background classes based on theme
  const mainBgClass = 'bg-transparent';
  const containerBgClass = isDark ? 'bg-white/10' : 'bg-white';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const conversationItemHoverClass = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100';
  const conversationItemSelectedClass = isDark ? 'bg-white/10' : 'bg-gray-100';
  const emptyStateBgClass = isDark ? 'bg-white/5' : 'bg-gray-50';
  const inputBgClass = isDark ? 'bg-white/5' : 'bg-white';
  const inputBorderClass = isDark ? 'border-white/10' : 'border-gray-300';
  const inputTextClass = isDark ? 'text-white' : 'text-gray-800';
  const iconClass = isDark ? 'text-gray-400' : 'text-gray-500';
  const iconHoverClass = isDark ? 'hover:text-white' : 'hover:text-gray-800';
  const filePreviewBgClass = isDark ? 'bg-white/5' : 'bg-gray-100';
  const fileIconBgClass = isDark ? 'bg-white/10' : 'bg-gray-200';
  const errorBgClass = isDark ? 'bg-red-500/20' : 'bg-red-100';
  const errorBorderClass = isDark ? 'border-red-500/30' : 'border-red-200';
  const errorTextClass = isDark ? 'text-white' : 'text-red-800';
  const avatarBgClass = isDark ? 'bg-white/10' : 'bg-gray-200';
  const messageBubbleFromOtherClass = isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-800';
  const messageBubbleTimeClass = isDark ? 'text-white/70' : 'text-gray-500';
  return <div className={`flex flex-col ${mainBgClass} ${textColorClass} font-sans transition-colors duration-300 overflow-x-hidden w-full max-w-full`} style={{
    height: 'calc(100svh - 80px)',
    overscrollBehaviorX: 'none'
  }} data-id="element-7505">
      <div className="flex items-center justify-between mb-4 shrink-0 max-w-7xl w-full mx-auto px-2" data-id="element-7506">
        <h1 className={`text-3xl md:text-4xl font-serif font-bold ${textColorClass}`} data-id="element-7507">
          Messages
        </h1>
      </div>
      {loading ? <div className="flex-1" aria-hidden="true" data-id="element-7508" /> : error ? <div className="max-w-7xl w-full mx-auto px-2 shrink-0" data-id="element-7509">
          <div className={`${errorBgClass} border ${errorBorderClass} rounded-lg p-4 mb-4`} data-id="element-7510">
            <p className={errorTextClass} data-id="element-7511">{error}</p>
          </div>
        </div> : <div className={`flex-1 min-h-0 max-w-7xl w-full mx-auto px-2 ${containerBgClass} backdrop-blur-sm rounded-xl overflow-hidden border ${borderClass}`} data-id="element-7512">
          <div className="flex h-full" data-id="element-7513">
            {/* Conversation List - only shown on desktop or when showConversationList is true on mobile */}
            {(!isMobileView || showConversationList) && <div className={`w-full md:w-1/3 border-r ${borderClass} overflow-y-auto`} data-id="element-7514">
                <div className={`p-4 border-b ${borderClass}`} data-id="element-7515">
                  <h2 className={`text-lg font-medium ${textColorClass}`} data-id="element-7516">
                    Conversations
                  </h2>
                  {/* Inbox / Archived tabs */}
                  <div className={`mt-3 flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} data-id="element-7517">
                    <button type="button" onClick={() => {
                setArchivedView(false);
                setMenuOpenForId(null);
              }} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${!archivedView ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7518">
                      <InboxIcon className="h-3.5 w-3.5" data-id="element-7519" />
                      Inbox
                      <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${!archivedView ? isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600' : isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-200 text-gray-500'}`} data-id="element-7520">
                        {activeConversations.length}
                      </span>
                    </button>
                    <button type="button" onClick={() => {
                setArchivedView(true);
                setMenuOpenForId(null);
              }} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${archivedView ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7521">
                      <ArchiveIcon className="h-3.5 w-3.5" data-id="element-7522" />
                      Archived
                      <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${archivedView ? isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600' : isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-200 text-gray-500'}`} data-id="element-7523">
                        {archivedConversations.length}
                      </span>
                    </button>
                  </div>
                </div>
                {visibleBucket.length === 0 ? <div className="p-6 text-center" data-id="element-7524">
                    {archivedView ? <>
                        <p className={lightTextColorClass} data-id="element-7525">
                          No archived conversations
                        </p>
                        <p className={`text-sm ${extraLightTextColorClass} mt-2`} data-id="element-7526">
                          Conversations you archive will appear here. They stay
                          safely stored and can be restored anytime.
                        </p>
                      </> : <>
                        <p className={lightTextColorClass} data-id="element-7527">
                          No conversations yet
                        </p>
                        <p className={`text-sm ${extraLightTextColorClass} mt-2`} data-id="element-7528">
                          Find a teacher to start a conversation
                        </p>
                      </>}
                  </div> : <ul className="px-2 py-2 space-y-1" data-id="element-7529">
                    {visibleBucket.map(conversation => <li key={conversation.id} className={`relative rounded-xl cursor-pointer transition-colors ${selectedConversation?.id === conversation.id ? conversationItemSelectedClass : conversationItemHoverClass}`} onClick={() => handleSelectConversation(conversation)} data-id="element-7530">
                        <div className="px-3 py-3 flex items-center" data-id="element-7531">
                          <div className={`h-12 w-12 rounded-full overflow-hidden ${avatarBgClass} flex-shrink-0 mr-3`} data-id="element-7532">
                            {conversation.teacherAvatar ? <img src={conversation.teacherAvatar} alt={conversation.teacherName} className="h-full w-full object-cover" data-id="element-7533" /> : <div className={`h-full w-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-7534">
                                <UserIcon className={`h-6 w-6 ${iconClass}`} data-id="element-7535" />
                              </div>}
                          </div>
                          <div className="flex-1 min-w-0 pr-6" data-id="element-7536">
                            <div className="flex justify-between items-center mb-1" data-id="element-7537">
                              <h3 className={`${textColorClass} font-medium truncate`} data-id="element-7538">
                                {conversation.teacherName}
                              </h3>
                              <span className={`text-xs ${extraLightTextColorClass}`} data-id="element-7539">
                                {conversation.lastMessageTimestamp ? formatTime(conversation.lastMessageTimestamp) : ''}
                              </span>
                            </div>
                            <div className="flex items-center" data-id="element-7540">
                              <p className={`text-sm ${lightTextColorClass} truncate`} data-id="element-7541">
                                {conversation.lastMessage || 'Start a conversation'}
                              </p>
                              {conversation.unread && <span className="ml-2 h-2 w-2 bg-deep-pink rounded-full" data-id="element-7542"></span>}
                            </div>
                          </div>
                        </div>
                        {/* 3-dot menu */}
                        <div data-conv-menu className="absolute top-2 right-2" data-id="element-7543">
                          <button type="button" onClick={e => {
                  e.stopPropagation();
                  setMenuOpenForId(menuOpenForId === conversation.id ? null : conversation.id);
                }} aria-label="Conversation options" disabled={actioningId === conversation.id} className={`p-1 rounded-md transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'} ${actioningId === conversation.id ? 'opacity-50' : ''}`} data-id="element-7544">
                            <MoreVerticalIcon className="h-4 w-4" data-id="element-7545" />
                          </button>
                          {menuOpenForId === conversation.id && <div onClick={e => e.stopPropagation()} className={`absolute right-0 top-6 z-20 whitespace-nowrap rounded-md shadow-md ring-1 ${isDark ? 'bg-navy-800 ring-white/10' : 'bg-white ring-black/5'} p-0.5`} data-id="element-7546">
                              {archivedView ? <button type="button" onClick={e => {
                    e.stopPropagation();
                    handleUnarchiveConversation(conversation.id);
                  }} className={`w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-left rounded-[5px] transition-colors ${isDark ? 'text-gray-200 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-7547">
                                  <ArchiveRestoreIcon className="h-3.5 w-3.5" data-id="element-7548" />
                                  Unarchive
                                </button> : <button type="button" onClick={e => {
                    e.stopPropagation();
                    handleArchiveConversation(conversation.id);
                  }} className={`w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-left rounded-[5px] transition-colors ${isDark ? 'text-gray-200 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-7549">
                                  <ArchiveIcon className="h-3.5 w-3.5" data-id="element-7550" />
                                  Archive
                                </button>}
                            </div>}
                        </div>
                      </li>)}
                  </ul>}
              </div>}
            {/* Message Area - only shown on desktop or when showConversationList is false on mobile */}
            {(!isMobileView || !showConversationList) && <div className="w-full md:w-2/3 flex flex-col min-h-0" data-id="element-7551">
                {selectedConversation ? <>
                    {/* Conversation Header */}
                    <div className={`p-4 border-b ${borderClass} flex items-center shrink-0`} data-id="element-7552">
                      {isMobileView && <button onClick={handleBackToList} className={`mr-2 ${iconClass} ${iconHoverClass}`} data-id="element-7553">
                          <ChevronLeftIcon className="h-5 w-5" data-id="element-7554" />
                        </button>}
                      <div className={`h-10 w-10 rounded-full overflow-hidden ${avatarBgClass} mr-3`} data-id="element-7555">
                        {selectedConversation.teacherAvatar ? <img src={selectedConversation.teacherAvatar} alt={selectedConversation.teacherName} className="h-full w-full object-cover" data-id="element-7556" /> : <div className={`h-full w-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-7557">
                            <UserIcon className={`h-5 w-5 ${iconClass}`} data-id="element-7558" />
                          </div>}
                      </div>
                      <div className="flex-1" data-id="element-7559">
                        <h3 className={`${textColorClass} font-medium`} data-id="element-7560">
                          {selectedConversation.teacherName}
                        </h3>
                        {selectedConversation.instrument && <p className={`text-xs ${extraLightTextColorClass}`} data-id="element-7561">
                            {selectedConversation.instrument} Teacher
                          </p>}
                      </div>
                    </div>
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 min-h-0 w-full max-w-full" style={{
              visibility: messagesLoading ? 'hidden' : 'visible',
              overscrollBehaviorX: 'none',
              WebkitOverflowScrolling: 'touch'
            }} ref={messageContainerRef} data-id="element-7562">
                      {messagesLoading ? null : messages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-7563">
                          <div className={`${emptyStateBgClass} rounded-full p-4 mb-3`} data-id="element-7564">
                            <MessageSquareIcon className={`h-8 w-8 ${iconClass}`} data-id="element-7565" />
                          </div>
                          <h3 className={`${textColorClass} font-medium mb-1`} data-id="element-7566">
                            Start a conversation
                          </h3>
                          <p className={`text-sm ${extraLightTextColorClass} max-w-xs`} data-id="element-7567">
                            Send a message to {selectedConversation.teacherName}{' '}
                            to discuss lessons or ask questions.
                          </p>
                        </div> : messages.map(message => {
                const isFromCurrentUser = message.senderId === currentUser.uid;
                return <div key={message.id} className={`flex ${isFromCurrentUser ? 'justify-end' : 'justify-start'}`} data-id="element-7568">
                              <div className={`max-w-[75%] rounded-lg px-4 py-2 break-words [overflow-wrap:anywhere] ${isFromCurrentUser ? 'bg-blue-500 text-white' : messageBubbleFromOtherClass}`} data-id="element-7569">
                                {message.text && <p data-id="element-7570">{message.text}</p>}
                                {message.attachmentUrl && <div className="mt-2" data-id="element-7571">
                                    {message.attachmentType === 'image' ? <div className="mt-1 w-[240px] h-[180px] rounded overflow-hidden bg-black/5 dark:bg-white/5 flex items-center justify-center" data-id="element-7572">
                                        <img src={message.attachmentUrl} alt="Attachment" className="w-full h-full rounded object-cover cursor-pointer hover:opacity-90 transition-opacity" loading="lazy" decoding="async" onClick={() => setLightboxImage(message.attachmentUrl!)} onLoad={() => {
                          if (isFromCurrentUser && !messagesLoading) {
                            scrollToBottom(true);
                          }
                        }} data-id="element-7573" />
                                      </div> : <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center ${filePreviewBgClass} rounded p-2 text-sm`} data-id="element-7574">
                                        <FileIcon className="h-4 w-4 mr-2 text-red-500" data-id="element-7575" />
                                        <span className={`truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7576">
                                          {message.attachmentName || 'Attachment'}
                                        </span>
                                      </a>}
                                  </div>}
                                <div className={`text-xs mt-1 ${isFromCurrentUser ? 'text-white/70' : extraLightTextColorClass}`} data-id="element-7577">
                                  {formatTime(message.timestamp)}
                                </div>
                              </div>
                            </div>;
              })}
                      <div ref={messagesEndRef} data-id="element-7578" />
                    </div>
                    {/* Message Input - Fixed at bottom */}
                    <div className={`p-4 border-t ${borderClass} shrink-0`} data-id="element-7579">
                      {selectedFile && <div className={`mb-2 ${filePreviewBgClass} rounded-lg p-2 flex items-center`} data-id="element-7580">
                          {filePreview ? <img src={filePreview} alt="Preview" className="h-16 w-16 object-cover rounded mr-2" data-id="element-7581" /> : <div className={`h-16 w-16 ${fileIconBgClass} rounded flex items-center justify-center mr-2`} data-id="element-7582">
                              <FileIcon className={`h-6 w-6 ${iconClass}`} data-id="element-7583" />
                            </div>}
                          <div className="flex-1 min-w-0" data-id="element-7584">
                            <p className={`text-sm ${textColorClass} truncate`} data-id="element-7585">
                              {selectedFile.name}
                            </p>
                            <p className={`text-xs ${extraLightTextColorClass}`} data-id="element-7586">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <button onClick={handleRemoveFile} className={`${iconClass} ${iconHoverClass}`} data-id="element-7587">
                            <XIcon className="h-5 w-5" data-id="element-7588" />
                          </button>
                        </div>}
                      <form onSubmit={handleSendMessage} className="flex items-center" data-id="element-7589">
                        <button type="button" className={`${iconClass} ${iconHoverClass} mr-2`} onClick={() => fileInputRef.current?.click()} disabled={isSending} data-id="element-7590">
                          <PaperclipIcon className="h-5 w-5" data-id="element-7591" />
                        </button>
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.mp3,.mp4" data-id="element-7592" />
                        <input type="text" ref={messageInputRef} value={newMessage} onChange={handleMessageInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} placeholder={isSending ? 'Sending...' : 'Type a message...'} className={`flex-1 ${inputBgClass} border ${inputBorderClass} rounded-lg px-4 py-2 ${inputTextClass} focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400`} disabled={isSending} data-id="element-7593" />
                        <button type="submit" disabled={!newMessage.trim() && !selectedFile || isSending} className={`ml-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 ${!newMessage.trim() && !selectedFile || isSending ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-7594">
                          <SendIcon className="h-5 w-5" data-id="element-7595" />
                        </button>
                      </form>
                    </div>
                  </> : <div className="h-full flex flex-col items-center justify-center text-center p-6" data-id="element-7596">
                    <div className={`${emptyStateBgClass} rounded-full p-6 mb-4`} data-id="element-7597">
                      <MessageSquareIcon className={`h-10 w-10 ${iconClass}`} data-id="element-7598" />
                    </div>
                    <h3 className={`text-xl font-medium ${textColorClass} mb-2`} data-id="element-7599">
                      Your Messages
                    </h3>
                    <p className={`${subtextColorClass} max-w-md mb-6`} data-id="element-7600">
                      Select a conversation from the list or find a teacher to
                      start messaging.
                    </p>
                    <button onClick={() => navigate('/find-teachers')} className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors" data-id="element-7601">
                      Find Teachers
                    </button>
                  </div>}
              </div>}
          </div>
        </div>}
      {/* Image Lightbox */}
      {lightboxImage && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] animate-in fade-in duration-200" onClick={() => setLightboxImage(null)} onKeyDown={e => {
      if (e.key === 'Escape') setLightboxImage(null);
    }} tabIndex={0} ref={el => el?.focus()} data-id="element-7602">
          <button onClick={e => {
        e.stopPropagation();
        setLightboxImage(null);
      }} className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10" aria-label="Close lightbox" data-id="element-7603">
            <XIcon className="h-8 w-8" data-id="element-7604" />
          </button>
          <img src={lightboxImage} alt="Full size attachment" className="max-w-[90%] max-h-[90%] rounded-lg object-contain" onClick={e => e.stopPropagation()} data-id="element-7605" />
        </div>}
    </div>;
};